import React, { useEffect, useRef, useState } from "react";

// A small, dependency-free rich text editor for the legal document HTML
// fields — a toolbar of common formatting buttons over a contentEditable
// area, using the browser's built-in execCommand. That API is old and
// technically deprecated, but every major browser still supports exactly
// this basic formatting use case, and it avoids pulling in an external
// editor library we can't verify builds cleanly here.
export default function HtmlEditor({ value, onChange, disabled = false, rows = 10 }) {
  const editorRef = useRef(null);
  // SOURCE VIEW, added 11 Sep 2026 for the email templates.
  //
  // Rich text is the right default for writing, and there are things it
  // cannot express. Email HTML in particular wants exact markup —
  // inline styles, a table, an {{unsubscribeLink}} inside an href — and
  // execCommand will not produce any of it. The toggle means the WYSIWYG
  // never becomes a ceiling.
  //
  // The two views share ONE value, so there is nothing to keep in step:
  // the source box edits the same HTML the editor renders.
  const [showSource, setShowSource] = useState(false);

  // Only sync innerHTML in from `value` when it actually changes from
  // outside (e.g. loading saved content) — syncing on every render would
  // reset the cursor position while typing.
  //
  // AND WHEN THE SOURCE VIEW CLOSES, which is why showSource is in the
  // dependencies. Switching views UNMOUNTS this div and remounts an
  // empty one; with only `value` here the effect did not re-run, so the
  // rich text view came back blank — and the first keystroke in it fired
  // onChange("") and wiped the real content, which then emptied the
  // source view too. One missing dependency, two visible faults.
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || "")) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value, showSource]);

  const exec = (command, arg) => {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    onChange(editorRef.current?.innerHTML || "");
  };

  const insertLink = () => {
    const url = window.prompt("Link URL:");
    if (url) exec("createLink", url);
  };

  const buttonStyle = {
    background: "var(--bg-panel-alt)",
    border: "1px solid var(--border-color-light)",
    borderRadius: 4,
    color: "var(--text-primary)",
    cursor: "pointer",
    padding: "4px 8px",
    fontSize: 12,
  };

  const toggleStyle = {
    background: showSource ? "var(--accent-blue, #1f5f8b)" : "var(--bg-panel-alt)",
    border: "1px solid var(--border-color-light)",
    borderRadius: 4,
    color: showSource ? "#fff" : "var(--text-primary)",
    cursor: "pointer",
    padding: "4px 8px",
    fontSize: 12,
    marginLeft: "auto",
  };

  return (
    <div style={{ border: "1px solid var(--border-color-light)", borderRadius: 6, overflow: "hidden", opacity: disabled ? 0.55 : 1 }}>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", padding: 6, background: "var(--bg-toolbar)", borderBottom: "1px solid var(--border-color-light)" }}>
        <button type="button" disabled={disabled || showSource} onClick={() => exec("bold")} style={{ ...buttonStyle, fontWeight: 700 }} title="Bold">B</button>
        <button type="button" disabled={disabled || showSource} onClick={() => exec("italic")} style={{ ...buttonStyle, fontStyle: "italic" }} title="Italic">I</button>
        <button type="button" disabled={disabled || showSource} onClick={() => exec("underline")} style={{ ...buttonStyle, textDecoration: "underline" }} title="Underline">U</button>
        <button type="button" disabled={disabled || showSource} onClick={() => exec("formatBlock", "H2")} style={buttonStyle} title="Heading">H2</button>
        <button type="button" disabled={disabled || showSource} onClick={() => exec("formatBlock", "H3")} style={buttonStyle} title="Subheading">H3</button>
        <button type="button" disabled={disabled || showSource} onClick={() => exec("formatBlock", "P")} style={buttonStyle} title="Paragraph">¶</button>
        <button type="button" disabled={disabled || showSource} onClick={() => exec("insertUnorderedList")} style={buttonStyle} title="Bullet list">• List</button>
        <button type="button" disabled={disabled || showSource} onClick={() => exec("insertOrderedList")} style={buttonStyle} title="Numbered list">1. List</button>
        <button type="button" disabled={disabled || showSource} onClick={insertLink} style={buttonStyle} title="Insert link">Link</button>
        <button type="button" disabled={disabled || showSource} onClick={() => exec("removeFormat")} style={buttonStyle} title="Clear formatting">Clear</button>
        <button
          type="button"
          onClick={() => setShowSource((s) => !s)}
          style={toggleStyle}
          title={showSource ? "Back to the formatted view" : "Edit the HTML directly"}
        >
          {showSource ? "Rich text" : "HTML"}
        </button>
      </div>
      {showSource ? (
        // A PLAIN TEXTAREA, deliberately. The point of this view is to
        // see and write the exact markup, so anything that reformats or
        // prettifies it would defeat it.
        <textarea
          value={value || ""}
          disabled={disabled}
          rows={rows}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          style={{
            width: "100%",
            minHeight: 140,
            padding: 10,
            background: "var(--bg-input)",
            color: "var(--text-primary)",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 12.5,
            lineHeight: 1.5,
            border: "none",
            outline: "none",
            boxSizing: "border-box",
            resize: "vertical",
          }}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable={!disabled}
          onInput={(e) => onChange(e.currentTarget.innerHTML)}
          style={{
            minHeight: 140,
            maxHeight: 320,
            overflowY: "auto",
            padding: 10,
            background: "var(--bg-input)",
            color: "var(--text-primary)",
            fontSize: 13,
            lineHeight: 1.5,
            outline: "none",
          }}
        />
      )}
    </div>
  );
}
